import { CSVParsedResponse } from "types/Models/reconciliation";
import { emptyApi } from "./emptyApi";
import { Transactions } from "types/Models/books";
import { Pagination } from "types/Models/pagination";
import { stringify } from "qs";

export type ReconciliationTxn = {
  amount: number;
  description: string;
  date: string;
  source: "CSV" | "INKLE_BOOKS" | "MANUAL";
  txn_uuid?: string;
  duplicate_with_id?: string;
  merchant?: string;
};

export type ReconcileResult = {
  as_of_start_date_balance: number;
  bank_csv_id: string;
  entity_bank_account_id: string;
  inkle_total_balance: number;
  last_reconcilation_status: string;
  money_in: number;
  money_out: number;
  reconciliation_end_date: string;
  reconciliation_start_date: string;
  statement_ending_balance: number;
  statement_starting_balance: number;
  transactions: Transactions[];
};

type ReconcileStateResult = {
  statement_ending_balance: number;
  entity_bank_account_id: string;
  reconcilation_end_date: string;
  reconcilation_start_date: string;
  inkle_total_balance: number;
  missing_txns: ReconciliationTxn[];
  duplicate_txns: ReconciliationTxn[];
  unaccounted_txns: [];
};
type UpdateStatementBalanceResult = {
  bank_csv_id: string;
  entity_id: string;
  redis_store_key: string;
  reconcilation_start_date: string;
  reconcilation_end_date: string;
  last_reconcilation_status: "ONGOING" | "COMPLETED" | "PENDING" | "EXPIRED";
  customer_ending_balance: number;
  entity_bank_account_id: string;
};

type statementTxn = { date: string; description: string; amount: number };

export type AIReconcileDetail = {
  reconciliation_state_id: string;
  last_reconcilation_status: "ONGOING" | "COMPLETED" | "PENDING" | "EXPIRED";
  bank_csv_id: string;
  entity_bank_account_id: string;
  reconciliation_start_date: string;
  reconciliation_end_date: string;
  statement_starting_balance: number;
  statement_ending_balance: number;
  inkle_total_balance: number;
  as_of_start_date_balance: number;
  money_in: number;
  money_out: number;
  account_name: string;
  bank_name: string;
  total_statement_transactions: number;
  total_period_statement_transactions: number;
  auto_reconciled: { statement_txn: statementTxn; books_txn: Transactions }[];
  possible_matches: { statement_txn: statementTxn; books_txn: Transactions }[];
  missing_in_books: statementTxn[];
  potential_duplicates: Transactions[];
  still_unreconciled: Transactions[];
  manual_additions: Transactions[];
};

export type StripeReconcileFinding = {
  check: string;
  severity: "ERROR" | "WARNING" | "INFO";
  message: string;
  basis: "CASH" | "ACCRUAL" | null;
  expected: string | null;
  actual: string | null;
  details: Record<string, unknown>;
};

// Stripe accounts are reconciled against the uploaded Stripe balance summary
export type StripeReconcileDetail = {
  is_stripe_reconciliation: true;
  reconciliation_state_id: string;
  entity_bank_account_id: string;
  bank_name: string;
  account_name: string;
  reconcilation_start_date: string;
  reconcilation_end_date: string;
  customer_statement_starting_balance: string;
  customer_statement_ending_balance: string;
  last_reconcilation_status: AIReconcileDetail["last_reconcilation_status"];
  result: "RECONCILED" | "NEEDS_ATTENTION" | "NOT_RECONCILED";
  findings: StripeReconcileFinding[];
  metrics: {
    stripe_balance?: Record<"CASH" | "ACCRUAL", string>;
    merchant_processor_balance?: Record<"CASH" | "ACCRUAL", string>;
  };
};

export const isStripeReconcileDetail = (
  detail: unknown
): detail is StripeReconcileDetail =>
  Boolean(
    (detail as StripeReconcileDetail | undefined)?.is_stripe_reconciliation
  );

export type ReconciliationHistoryItem = {
  reconcilation_id: string;
  reconcilation_start_date: string;
  reconcilation_end_date: string;
  reconciled_by: string;
  last_reconciled_date: string;
  customer_statement_ending_balance: number;
  bank_csv_id: string | null;
  bank_account: {
    uuid: string;
    name: string;
    mask: string;
    account_type: string;
    is_credit_card: boolean;
    bank_brand: {
      name: string;
      logo_url: string;
    };
  };
};

export type ReconciliationHistoryResponse = Pagination & {
  reconciliations: ReconciliationHistoryItem[];
};

const extendedApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    uploadReconcileCSV: build.mutation<
      CSVParsedResponse,
      {
        groupId: string;
        entityId: string;
        payload: {
          entity_bank_account_id: string;
          reconcilation_ending_date: string;
          reconcilation_starting_date: string;
          customer_statement_ending_balance: string;
          csv?: File;
        };
      }
    >({
      query: ({ groupId, entityId, payload }) => {
        const body = new FormData();
        Object.entries(payload).forEach(([name, value]) => {
          body.append(name, value);
        });

        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/v2/upload_csv/`,
          method: "POST",
          body,
        };
      },
    }),
    uploadAiReconcileStatement: build.mutation<
      AIReconcileDetail,
      {
        groupId: string;
        entityId: string;
        // Stripe accounts send only the account and the balance summary PDF
        payload: {
          entity_bank_account_id: string;
          reconcilation_ending_date?: string;
          reconcilation_starting_date?: string;
          customer_statement_ending_balance?: string;
          csv: File;
        };
      }
    >({
      query: ({ groupId, entityId, payload }) => {
        const body = new FormData();
        Object.entries(payload).forEach(([name, value]) => {
          if (value !== undefined) {
            body.append(name, value);
          }
        });

        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/v2/ai_reconcile_upload/`,
          method: "POST",
          body,
        };
      },
    }),
    storeCSVtransactions: build.mutation<
      {
        message: string;
        reconcilation_state_id: string;
        redis_csv_store_key: string;
      },
      {
        groupId: string;
        entityId: string;
        bank_csv_id: string;
        date_field: string;
        description_field: string;
        merchant_field: string;
        amount_field: string;
        credit_field: string;
        debit_field: string;
        reconcilation_state_id: string;
      }
    >({
      query: ({
        groupId,
        entityId,
        date_field,
        description_field,
        merchant_field,
        amount_field,
        debit_field,
        credit_field,
        bank_csv_id,
        reconcilation_state_id,
      }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/reconcile_transactions/`,
          method: "POST",
          body: {
            date_field,
            description_field,
            merchant_field,
            amount_field,
            debit_field,
            credit_field,
            bank_csv_id,
            reconcilation_state_id,
          },
        };
      },
    }),
    getReconciliationStatus: build.query<
      ReconcileStateResult,
      { reconcilationId: string; groupId: string; entityId: string }
    >({
      query: ({ reconcilationId, entityId, groupId }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/perform_reconcilation/?reconcilation_state_id=${reconcilationId}`,
        };
      },
      providesTags: ["RECONCILIATION"],
    }),
    getReconciliationTxns: build.query<
      ReconcileResult,
      {
        reconcilationId: string;
        entityId: string;
        from_date: string;
        to_date: string;
        sort_col?: string;
        sort_order?: string;
      }
    >({
      query: ({
        reconcilationId,
        entityId,
        from_date,
        to_date,
        sort_col,
        sort_order,
      }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/get_reconcilation_status/${reconcilationId}`,
          params: {
            from_date,
            to_date,
            sort_col,
            sort_order,
          },
        };
      },
      providesTags: ["RECONCILIATION"],
    }),
    updateStatementBalance: build.mutation<
      UpdateStatementBalanceResult,
      {
        groupId: string;
        entityId: string;
        statementEndingBalance: string;
        reconcilationId: string;
      }
    >({
      query: ({
        groupId,
        entityId,
        statementEndingBalance,
        reconcilationId,
      }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/reconcilation_state/`,
          method: "PUT",
          body: {
            statement_ending_balance: statementEndingBalance,
            reconcilation_state_id: reconcilationId,
          },
        };
      },
      invalidatesTags: ["RECONCILIATION"],
    }),
    completeReconciliation: build.mutation<
      any,
      {
        groupId: string;
        entityId: string;
        reconcilationId: string;
        unreconciled_transaction_ids: string[];
        txn_ids_to_reconcile: string[];
        excluded_transaction_ids?: string[];
        manual_transactions?: {
          date: string;
          amount: number;
          description: string;
          merchant_data_id?: string;
          transaction_category_id?: string;
          is_added_from_reconciliation_table?: boolean;
        }[];
      }
    >({
      query: ({
        groupId,
        entityId,
        reconcilationId,
        unreconciled_transaction_ids,
        txn_ids_to_reconcile,
        excluded_transaction_ids,
        manual_transactions,
      }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/perform_reconcilation/`,
          method: "PUT",
          body: {
            reconcilation_state_id: reconcilationId,
            unreconciled_transaction_ids,
            txn_ids_to_reconcile,
            excluded_transaction_ids,
            manual_transactions,
          },
        };
      },
      invalidatesTags: ["RECONCILIATION", { type: "PlaidBanks", id: "LIST" }],
    }),
    updateReconciliationStartDate: build.mutation<
      any,
      {
        entityId: string;
        accountId: string;
        reconciliation_start_date: string;
        bookkeeping_start_date?: string;
      }
    >({
      query: ({
        entityId,
        accountId,
        reconciliation_start_date,
        bookkeeping_start_date,
      }) => {
        const body: {
          reconcilation_start_date: string;
          bookkeeping_start_date?: string;
        } = {
          reconcilation_start_date: reconciliation_start_date,
        };

        if (bookkeeping_start_date) {
          body.bookkeeping_start_date = bookkeeping_start_date;
        }

        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/entity_bank_account/${accountId}/reconcilations/`,
          method: "PATCH",
          body,
        };
      },
      invalidatesTags: ["RECONCILIATION", { type: "PlaidBanks", id: "LIST" }],
    }),
    getReconciliationHistory: build.query<
      ReconciliationHistoryResponse,
      {
        entityId: string;
        fromDate?: string;
        toDate?: string;
        entity_bank_account_ids?: string;
        page_num?: number;
      }
    >({
      query: ({
        entityId,
        fromDate,
        toDate,
        entity_bank_account_ids,
        page_num,
      }) => {
        const query = stringify(
          {
            from_date: fromDate || null,
            to_date: toDate || null,
            entity_bank_account_ids: entity_bank_account_ids || null,
            page: page_num || 1,
          },
          { skipNulls: true, addQueryPrefix: true }
        );

        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/bank_account_reconcilations/${query}`,
        };
      },
      providesTags: ["RECONCILIATION"],
    }),
    getAIReconcileDetail: build.query<
      AIReconcileDetail,
      { entityId: string; reconciliationStateId: string }
    >({
      query: ({ entityId, reconciliationStateId }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/ai_reconcile/${reconciliationStateId}/`,
      }),
      providesTags: ["RECONCILIATION"],
    }),
    exportReconciliation: build.mutation<
      {
        document_id: string;
        file_name: string;
        email_sent: boolean;
      },
      {
        entityId: string;
        reconciliation_ids: string;
        export_to_mail: boolean;
        folder_id?: string;
      }
    >({
      query: ({ entityId, reconciliation_ids, export_to_mail, folder_id }) => {
        const query = stringify(
          {
            reconciliation_ids,
            export_to_mail,
            folder_id: folder_id || null,
          },
          { skipNulls: true, addQueryPrefix: true }
        );

        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/reconciliation/export${query}`,
        };
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useUploadReconcileCSVMutation,
  useUploadAiReconcileStatementMutation,
  useStoreCSVtransactionsMutation,
  useGetReconciliationStatusQuery,
  useGetReconciliationTxnsQuery,
  useUpdateStatementBalanceMutation,
  useCompleteReconciliationMutation,
  useUpdateReconciliationStartDateMutation,
  useGetReconciliationHistoryQuery,
  useGetAIReconcileDetailQuery,
  useExportReconciliationMutation,
} = extendedApi;
